/**
 * Гард разделов кабинета агента (DEV-1265, ADR-0042).
 *
 * Решает ровно один вопрос — «рисовать ли экран». Доступ к данным решает право
 * на RPC внутри сервиса; гард его не заменяет и не дублирует.
 *
 * ⚠️ Отказ НЕ гасит сессию. Прежний ролевой гард делал logout() и уводил на
 * форму входа: человек залогинен корректно, а выглядело это как «сломался
 * вход», и он вводил тот же пароль по кругу. Та же ошибка была в CRM и
 * исправлена там же (DEV-1255).
 */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useSectionsStore } from '@/features/auth/stores/sections.store'

/** Экран отказа для недопущенного в кабинет: без меню, рисовать его нечем. */
export const NO_ACCESS_PATH = '/no-access'

export const sectionGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  // Публичные маршруты (вход, регистрация, оферта, восстановление пароля,
  // приглашение, подтверждение почты) гейтить нечем и незачем. Экран отказа
  // тоже: попадают на него именно с пустой картой, и он зациклился бы на себе.
  if (to.meta.requiresAuth !== true) {
    next()
    return
  }

  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  const sections = useSectionsStore()

  try {
    // Навигация ждёт карту: решение на промежуточном состоянии — это ровно та
    // гонка, из-за которой меню успевало отрисоваться по старым данным.
    await sections.ensureLoaded()
  } catch {
    // Сеть отвалилась — это не отказ в доступе. Запирать человека на экране
    // отказа из-за таймаута нельзя: данные всё равно защищает бэкенд, а стор
    // остался в статусе error и повторит попытку на следующем переходе.
    next()
    return
  }

  // Ни одного раздела при живом ответе сервера — принципал кабинету не
  // принадлежит. Внутрь не пускаем вовсе: меню было бы пустым, а экраны —
  // набором отказов.
  if (sections.status === 'ready' && sections.keys.length === 0) {
    if (to.path === NO_ACCESS_PATH) {
      next()
      return
    }

    next(NO_ACCESS_PATH)
    return
  }

  const section = to.meta.section

  // Fail-closed: приватный маршрут без раздела недоступен. Забытый
  // meta.section — это дефект, и он обязан выглядеть как дефект, а не как
  // «всем можно». Против забывчивости стоит routeCoverage.spec.ts.
  if (section === undefined) {
    next(NO_ACCESS_PATH)
    return
  }

  if (sections.hasSection(section)) {
    next()
    return
  }

  // Раздел есть в реестре фронта, но не в карте принципала. В кабинете это
  // означает то же, что и пустая карта: своих разделов у него нет.
  next(NO_ACCESS_PATH)
}
